import React from 'react';
import { Upload, Cog, Download, FileText, Search, Shield, Wand2, Users, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { Button } from '../ui/button';

const HowItWorksPage = () => {
  const steps = [
    {
      number: "01",
      icon: Upload,
      title: "Upload Your Document",
      description: "Start by uploading any business document — contracts, agreements, invoices, proposals, or any file you need to work with. We accept over 30 file types including PDF, Word, and many more. Simply drag and drop or click to browse.",
      details: [
        "Drag and drop or click to upload",
        "Support for 30+ file formats",
        "Files up to 100MB (Professional)",
        "Your documents stay private"
      ]
    },
    {
      number: "02",
      icon: Cog,
      title: "Choose What You Need",
      description: "Select from our easy-to-use tools. Convert to a different format, scan paper documents with OCR, or get an AI-powered plain-English explanation of complex terms. No legal expertise required.",
      details: [
        "Format conversion (PDF, Word, etc.)",
        "OCR for scanned documents",
        "AI-powered explanations",
        "PDF editing tools"
      ]
    },
    {
      number: "03",
      icon: Download,
      title: "Get Results Instantly",
      description: "Download your converted document or review your analysis in seconds. What would take hours waiting for a lawyer takes just moments here. Share with partners or keep for your records.",
      details: [
        "Instant download",
        "Secure sharing options",
        "Professional formatting",
        "Save 90% vs lawyer fees"
      ]
    }
  ];

  const features = [
    {
      icon: FileText,
      title: "Document Conversion",
      description: "Convert your contracts, invoices, and business documents between any formats. Need a PDF from a Word doc? Done. Need to edit a PDF in Word? Easy. We preserve all formatting perfectly.",
      howTo: "Upload your document, select 'Convert' tab, choose your target format, and click Convert. Your document will be processed in seconds with all formatting intact."
    },
    {
      icon: Search,
      title: "OCR Processing",
      description: "Have paper contracts or scanned documents? Our OCR technology extracts all the text and makes it searchable and editable. Supporting 15 languages with 99% accuracy.",
      howTo: "Upload a scanned image or PDF, navigate to 'OCR' tab, select your document language(s), and click Process. The system will extract all text while preserving the original document layout."
    },
    {
      icon: Wand2,
      title: "AI Document Analysis",
      description: "Confused by legal jargon in contracts? Our AI reads your documents and explains them in plain English. It highlights important dates, obligations, and potential risks you should know about.",
      howTo: "Upload your document and select 'AI Analysis' tab. Choose Quick Analysis for a summary or Deep Analysis for a detailed review. Results appear within seconds — no waiting for callbacks."
    },
    {
      icon: Shield,
      title: "PDF Security Tools",
      description: "Protect sensitive legal documents with password encryption, digital signatures, and redaction tools. Our security features meet legal industry standards for document protection.",
      howTo: "In the PDF Tools section, select your security operation: add passwords, apply digital signatures, or permanently redact sensitive information. All security features create legally compliant protected documents."
    },
    {
      icon: Users,
      title: "Real-Time Collaboration",
      description: "Work on documents simultaneously with colleagues. See changes as they happen, add annotations, leave comments, and track all modifications with complete audit trails.",
      howTo: "Open your document in the Collaboration tab, invite team members by email, and start editing together. Changes sync instantly, and all activity is logged for compliance purposes."
    },
    {
      icon: Clock,
      title: "Version History",
      description: "Never lose work with automatic version history. Access any previous version of your document, compare changes between versions, and restore earlier states when needed.",
      howTo: "Click 'Version History' on any document to see all saved versions. Use 'Compare' to see differences between versions or 'Restore' to revert to a previous state."
    }
  ];

  const useCases = [
    {
      title: "Vendor Contract Review",
      scenario: "A supplier just sent you a 30-page contract. You need to understand what you're signing before committing.",
      solution: "Upload the contract and run AI Analysis. Within seconds, get a plain-English summary of payment terms, liability clauses, termination rights, and any red flags. Make an informed decision without a $500 lawyer consultation."
    },
    {
      title: "Converting Documents for Clients",
      scenario: "A client sent you files in a format you can't open, or you need to provide documents in a specific format.",
      solution: "Upload any document type, select your desired output format, and convert instantly. Perfect for proposals, invoices, contracts — whatever your business needs."
    },
    {
      title: "Digitizing Old Business Records",
      scenario: "You have boxes of paper contracts, receipts, or records that need to be searchable and organized.",
      solution: "Scan your documents and upload them to our OCR processor. We extract all text and create searchable PDFs you can actually find things in. Great for tax prep or audits."
    },
    {
      title: "Collaborating with Business Partners",
      scenario: "You and your business partner need to review and finalize an operating agreement together.",
      solution: "Upload the document to Collaboration mode, invite your partner, and edit together in real-time. Track who made what changes, discuss via comments, and both sign off when you're satisfied."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            How LegalDocConverter Works
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Handle your business documents like a pro — no expensive lawyers needed. 
            Learn how to convert, understand, and manage your contracts in three easy steps.
          </p>
        </div>
      </section>

      {/* Three Steps Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-16">
            Three Simple Steps — No Legal Expertise Required
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-20 left-full w-full">
                    <ArrowRight className="h-8 w-8 text-blue-300 -ml-4" />
                  </div>
                )}
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-8 h-full border border-slate-100">
                  <div className="text-5xl font-bold text-blue-100 mb-4">{step.number}</div>
                  <div className="p-3 bg-blue-600 rounded-xl w-fit mb-4">
                    <step.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 mb-4">{step.description}</p>
                  <ul className="space-y-2">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Tutorials */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-4">
            Feature Guides
          </h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-16">
            Detailed instructions for each of our powerful document processing tools. Click any feature to learn exactly how to use it.
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="p-3 bg-blue-100 rounded-lg w-fit mb-4">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{feature.description}</p>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">How to use:</h4>
                    <p className="text-sm text-slate-600">{feature.howTo}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-4">
            Real-World Use Cases
          </h2>
          <p className="text-center text-slate-600 max-w-2xl mx-auto mb-16">
            See how legal professionals use LegalDocConverter to solve everyday document challenges.
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                <h3 className="text-lg font-bold text-slate-900 mb-3">{useCase.title}</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-xs font-semibold text-blue-600 uppercase">Scenario</span>
                    <p className="text-slate-600 text-sm">{useCase.scenario}</p>
                  </div>
                  <div>
                    <span className="text-xs font-semibold text-green-600 uppercase">Solution</span>
                    <p className="text-slate-600 text-sm">{useCase.solution}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">
            Pro Tips for Best Results
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold mb-2">For OCR</h3>
              <p className="text-slate-300 text-sm">Scan at 300+ DPI and ensure documents are straight. Color scans produce better results than black-and-white.</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold mb-2">For Conversion</h3>
              <p className="text-slate-300 text-sm">Use PDF/A format for any documents requiring long-term archival or court filing compliance.</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold mb-2">For AI Analysis</h3>
              <p className="text-slate-300 text-sm">Run Deep Analysis on contracts to identify all obligations, deadlines, and potential risk areas.</p>
            </div>
            <div className="bg-slate-800 rounded-lg p-6">
              <h3 className="font-semibold mb-2">For Collaboration</h3>
              <p className="text-slate-300 text-sm">Use named versions to mark significant milestones during multi-party document negotiation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            Try LegalDocConverter free today. No credit card required for basic features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-white text-blue-600 hover:bg-blue-50"
              onClick={() => window.location.href = '/#processor'}
            >
              Start Converting Now
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10"
              onClick={() => window.location.href = '/faq'}
            >
              Read FAQ
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowItWorksPage;
